/**
 * Shared price primitives for the dynamic-pricing service — the ONE place that
 * parses money, formats it, and decides whether an offer is a genuine promo.
 *
 * BK's v3 audit flagged that promo-detection + money formatting are reimplemented
 * ~6× across the legacy pricing adapters (flip-cards, plan-card, carousel, …). The
 * new service must not become the 7th copy: it depends only on these helpers, and
 * they are the intended convergence target when Phase-6 migrates the legacy
 * adapters onto the registry.
 */

/** Parse a price-ish value to a number, or null when not numeric / empty. */
export function toNumber(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const n = typeof value === "number" ? value : parseFloat(String(value));
  return Number.isNaN(n) ? null : n;
}

/**
 * Format a price to a fixed 2-decimal string WITHOUT a currency symbol
 * ("49.99", "60.00") — the CMS template supplies the `$` and the `/mo` period.
 * Returns null for non-numeric / empty input (→ self-suppress).
 *
 * NOTE: intentionally NOT auto-applied to every price yet — doing so would change
 * rendered strings (e.g. "60" → "60.00") and needs a visual/CMS pass first. It is
 * exposed here for deliberate adoption + Phase-6 convergence.
 */
export function formatPrice(value: string | number | null | undefined): string | null {
  const n = toNumber(value);
  return n === null ? null : n.toFixed(2);
}

/**
 * True when `regular` is a genuine discount over `offer` (both numeric and
 * regular > offer). Equal or lower → no promo, so the strikethrough must
 * self-hide. This is the single promo-detection rule the service resolves
 * through (see `suppressRedundantRegularPrice`).
 */
export function hasPromo(
  offer: string | number | null | undefined,
  regular: string | number | null | undefined
): boolean {
  const o = toNumber(offer);
  const r = toNumber(regular);
  return o !== null && r !== null && r > o;
}
